import { useState, useEffect, useRef, useCallback } from 'react';
import { DefaultService, PlayOutput, Player, PlayerWithLeagueContext, GameOutput, LeaguePlayer } from '../generated-api';
import { useData } from '../contexts/DataContext';
import { getRatingHistories } from '../services/ratingHistories';

interface RatingInfo {
  rating: number;
  sigma: number;
}

export interface PlayRecapData {
  play: PlayOutput | null;
  game: GameOutput | null;
  players: (Player | PlayerWithLeagueContext)[];
  // Per-league rating data (leagueId → playerId → value)
  leagueRatingChanges: Record<string, Record<string, number>>;
  leagueRatingsBefore: Record<string, Record<string, RatingInfo>>;
  leagueRatingsAfter: Record<string, Record<string, RatingInfo>>;
  leagueWinProbabilities: Record<string, Record<string, number>>;
  // Extended
  recentPlaysPerPlayer: Record<string, PlayOutput[]>;
  leagueStandings: Record<string, LeaguePlayer[]>;
  // Loading states
  coreLoading: boolean;
  ratingsLoading: boolean;
  ratingsReady: boolean;
  ratingsTimedOut: boolean;
  extendedLoading: boolean;
  error: string | null;
}

const RATING_RETRY_DELAYS = [2000, 4000, 8000];

export function usePlayRecapData(playId: string | undefined): PlayRecapData {
  const { fetchGames, fetchLeagues } = useData();

  const [play, setPlay] = useState<PlayOutput | null>(null);
  const [game, setGame] = useState<GameOutput | null>(null);
  const [players, setPlayers] = useState<(Player | PlayerWithLeagueContext)[]>([]);
  const [leagueRatingChanges, setLeagueRatingChanges] = useState<Record<string, Record<string, number>>>({});
  const [leagueRatingsBefore, setLeagueRatingsBefore] = useState<Record<string, Record<string, RatingInfo>>>({});
  const [leagueRatingsAfter, setLeagueRatingsAfter] = useState<Record<string, Record<string, RatingInfo>>>({});
  const [leagueWinProbabilities, setLeagueWinProbabilities] = useState<Record<string, Record<string, number>>>({});
  const [recentPlaysPerPlayer, setRecentPlaysPerPlayer] = useState<Record<string, PlayOutput[]>>({});
  const [leagueStandings, setLeagueStandings] = useState<Record<string, LeaguePlayer[]>>({});
  const [coreLoading, setCoreLoading] = useState(true);
  const [ratingsLoading, setRatingsLoading] = useState(true);
  const [ratingsReady, setRatingsReady] = useState(false);
  const [ratingsTimedOut, setRatingsTimedOut] = useState(false);
  const [extendedLoading, setExtendedLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef(false);

  // Phase 1: Core data
  const loadCoreData = useCallback(async () => {
    if (!playId) return null;
    try {
      const [playData, playersData, gamesData] = await Promise.all([
        DefaultService.getPlayPlaysPlayIdGet({ playId }),
        DefaultService.getPlayersPlayersGet(),
        fetchGames(),
        fetchLeagues(),
      ]);
      if (abortRef.current) return null;
      setPlay(playData);
      setPlayers(playersData);
      const foundGame = gamesData.find(g => g.id === playData.gameId) || null;
      setGame(foundGame);
      setCoreLoading(false);
      return { playData, playersData };
    } catch (err) {
      console.error('Error loading core recap data:', err);
      setError('Failed to load play data.');
      setCoreLoading(false);
      return null;
    }
  }, [playId, fetchGames, fetchLeagues]);

  // Phase 2: Ratings with retry/backoff
  const loadRatings = useCallback(async (playData: PlayOutput) => {
    if (!playData.leagueIds?.length) {
      setRatingsLoading(false);
      setRatingsReady(true);
      return;
    }

    const allPlayerIds = Object.values(playData.teams).flat();

    const fetchRatingsOnce = async (): Promise<boolean> => {
      const changes: Record<string, Record<string, number>> = {};
      const before: Record<string, Record<string, RatingInfo>> = {};
      const after: Record<string, Record<string, RatingInfo>> = {};

      for (const leagueId of playData.leagueIds) {
        changes[leagueId] = {};
        before[leagueId] = {};
        after[leagueId] = {};
      }

      // One request per league, always fresh: this retries until the just-logged play appears
      const leagueHistories = await Promise.all(
        playData.leagueIds.map(leagueId =>
          getRatingHistories(leagueId, { playerIds: allPlayerIds, fresh: true }).catch(() => null)
        )
      );

      let foundCount = 0;
      const expectedCount = allPlayerIds.length * playData.leagueIds.length;

      playData.leagueIds.flatMap((leagueId, index) =>
        allPlayerIds.map(playerId => ({ leagueId, playerId, ratingHistory: leagueHistories[index]?.[playerId] }))
      ).forEach(({ leagueId, playerId, ratingHistory }) => {
        if (!ratingHistory) return;

        const progressionIndex = ratingHistory.ratingProgression?.findIndex(
          (entry: any) => entry.playId === playData.id
        );

        if (progressionIndex !== undefined && progressionIndex >= 0) {
          foundCount++;
          const entry = ratingHistory.ratingProgression[progressionIndex];
          changes[leagueId][playerId] = Math.round(entry.ratingChange * 100) / 100;

          const ratingAfterVal = entry.ratingAfter.rating;
          const sigmaAfter = entry.ratingAfter.sigma;
          const ratingBeforeVal = ratingAfterVal - entry.ratingChange;

          let sigmaBefore: number;
          if (progressionIndex > 0) {
            sigmaBefore = ratingHistory.ratingProgression[progressionIndex - 1].ratingAfter.sigma;
          } else {
            sigmaBefore = 8.333;
          }

          before[leagueId][playerId] = {
            rating: Math.round(ratingBeforeVal * 100) / 100,
            sigma: Math.round(sigmaBefore * 100) / 100,
          };
          after[leagueId][playerId] = {
            rating: Math.round(ratingAfterVal * 100) / 100,
            sigma: Math.round(sigmaAfter * 100) / 100,
          };
        }
      });

      if (abortRef.current) return true;
      setLeagueRatingChanges(changes);
      setLeagueRatingsBefore(before);
      setLeagueRatingsAfter(after);

      return foundCount > 0 || foundCount === expectedCount;
    };

    // Fetch win probabilities in parallel
    // API returns { teamName: percentage }, remap to { playerId: percentage }
    const fetchWinProbs = async () => {
      const probs: Record<string, Record<string, number>> = {};
      const requests = playData.leagueIds.map(leagueId => ({
        leagueId,
        promise: DefaultService.getPlayWinProbabilitiesPlaysPlayIdWinProbabilitiesGet({
          playId: playData.id,
          leagueId,
        }).catch(() => ({})),
      }));
      const results = await Promise.all(requests.map(r => r.promise));
      results.forEach((teamProbs, index) => {
        const leagueId = requests[index].leagueId;
        const playerProbs: Record<string, number> = {};
        // Map team probabilities to individual players
        for (const [teamName, probability] of Object.entries(teamProbs)) {
          const playerIds = playData.teams[teamName] || [];
          for (const pid of playerIds) {
            playerProbs[pid] = probability as number;
          }
        }
        probs[leagueId] = playerProbs;
      });
      if (!abortRef.current) setLeagueWinProbabilities(probs);
    };

    // Attempt rating fetch with retries
    let found = await fetchRatingsOnce();
    fetchWinProbs();

    if (!found) {
      for (const delay of RATING_RETRY_DELAYS) {
        if (abortRef.current) break;
        await new Promise(resolve => setTimeout(resolve, delay));
        if (abortRef.current) break;
        found = await fetchRatingsOnce();
        if (found) break;
      }
    }

    if (!abortRef.current) {
      setRatingsLoading(false);
      setRatingsReady(true);
      setRatingsTimedOut(!found);
    }
  }, []);

  // Phase 3: Extended data (recent plays per player, league standings)
  const loadExtendedData = useCallback(async (playData: PlayOutput) => {
    const allPlayerIds = Object.values(playData.teams).flat();

    try {
      // Recent plays per player at this game
      const recentPlaysPromises = allPlayerIds.map(playerId =>
        DefaultService.getPlaysPlaysGet({
          playerId,
          gameId: playData.gameId,
          limit: 20,
          sort: 'newest',
        }).catch(() => [] as PlayOutput[])
      );

      // League standings
      const standingsPromises = playData.leagueIds.map(leagueId =>
        DefaultService.getLeaguePlayersLeaguesLeagueIdPlayersGet({ leagueId })
          .catch(() => [] as LeaguePlayer[])
      );

      const [recentResults, standingsResults] = await Promise.all([
        Promise.all(recentPlaysPromises),
        Promise.all(standingsPromises),
      ]);

      if (abortRef.current) return;

      const recentMap: Record<string, PlayOutput[]> = {};
      allPlayerIds.forEach((playerId, idx) => {
        recentMap[playerId] = recentResults[idx];
      });

      const standingsMap: Record<string, LeaguePlayer[]> = {};
      playData.leagueIds.forEach((leagueId, idx) => {
        standingsMap[leagueId] = standingsResults[idx];
      });

      setRecentPlaysPerPlayer(recentMap);
      setLeagueStandings(standingsMap);
    } catch (err) {
      console.error('Error loading extended recap data:', err);
    } finally {
      if (!abortRef.current) setExtendedLoading(false);
    }
  }, []);

  useEffect(() => {
    abortRef.current = false;
    setCoreLoading(true);
    setRatingsLoading(true);
    setRatingsReady(false);
    setRatingsTimedOut(false);
    setExtendedLoading(true);
    setError(null);

    (async () => {
      const coreResult = await loadCoreData();
      if (!coreResult || abortRef.current) return;

      // Phase 2 and Phase 3 run in parallel
      loadRatings(coreResult.playData);
      loadExtendedData(coreResult.playData);
    })();

    return () => {
      abortRef.current = true;
    };
  }, [playId, loadCoreData, loadRatings, loadExtendedData]);

  return {
    play,
    game,
    players,
    leagueRatingChanges,
    leagueRatingsBefore,
    leagueRatingsAfter,
    leagueWinProbabilities,
    recentPlaysPerPlayer,
    leagueStandings,
    coreLoading,
    ratingsLoading,
    ratingsReady,
    ratingsTimedOut,
    extendedLoading,
    error,
  };
}
